import React, { useEffect, useState, useMemo } from "react";
import useSEO from "../lib/useSEO";
import { apiClient } from "../lib/api";
import { useLanguage } from "../lib/LanguageContext";

const SERVICE_FILTERS = ["Property Management", "Estate / Home Oversight", "Preventive Maintenance", "Hurricane & Storm Preparation", "Maintenance & Construction", "Concierge Services", "Luxury Rental Oversight"];

export default function Testimonials() {
    const { t } = useLanguage();
    useSEO({
        title: t("testimonialsPage.seoTitle"),
        description: t("testimonialsPage.seoDescription"),
        path: "/testimonials",
        keywords: "Pacific Key Management reviews, property management Los Cabos testimonials",
        breadcrumbs: [{ name: "Home", path: "/" }, { name: t("testimonialsPage.heading"), path: "/testimonials" }],
    });
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const ALL = t("testimonialsPage.filterAll");
    const [filter, setFilter] = useState("__all__");

    useEffect(() => {
        try { window.gtag && window.gtag("event", "testimonials_viewed"); } catch (e) {}
        apiClient.get("/testimonials/public").then(({ data }) => setItems(data || [])).catch(() => setItems([])).finally(() => setLoading(false));
    }, []);

    const services = useMemo(() => new Set(items.map((i) => i.service_category).filter(Boolean)), [items]);
    const showFilters = items.length >= 4 && services.size >= 2;
    const visible = filter === "__all__" ? items : items.filter((i) => i.service_category === filter);

    return (
        <div className="bg-cream min-h-screen py-20 sm:py-28 px-6" data-testid="testimonials-page">
            <div className="max-w-5xl mx-auto">
                <div className="max-w-2xl mb-16">
                    <p className="overline text-gold mb-4">{t("testimonialsPage.eyebrow")}</p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy mb-6" data-testid="testimonials-heading">{t("testimonialsPage.heading")}</h1>
                    <p className="font-serif text-xl text-navy/85 italic mb-5">&ldquo;{t("testimonialsPage.quote")}&rdquo;</p>
                    <p className="text-navy/70 text-base leading-relaxed">{t("testimonialsPage.intro")}</p>
                </div>

                {showFilters && (
                    <div className="flex flex-wrap gap-3 mb-12" data-testid="testimonials-filters">
                        <button onClick={() => setFilter("__all__")} data-testid="filter-all"
                            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${filter === "__all__" ? "bg-navy text-cream border-navy" : "border-navy/20 text-navy/70 hover:border-gold"}`}>{ALL}</button>
                        {SERVICE_FILTERS.filter((f) => services.has(f)).map((f) => (
                            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                                className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${filter === f ? "bg-navy text-cream border-navy" : "border-navy/20 text-navy/70 hover:border-gold"}`}>{f}</button>
                        ))}
                    </div>
                )}

                {loading ? (
                    <p className="text-navy/50 text-sm">{t("testimonialsPage.loading")}</p>
                ) : visible.length === 0 ? (
                    <div className="border border-navy/10 bg-white p-12 text-center" data-testid="testimonials-empty">
                        <p className="font-serif text-2xl text-navy mb-3">{t("testimonialsPage.emptyTitle")}</p>
                        <p className="text-navy/60 text-sm max-w-md mx-auto">{t("testimonialsPage.emptyBody")}</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-8" data-testid="testimonials-grid">
                        {visible.map((tst) => (
                            <figure key={tst.id} className="bg-white border border-navy/10 p-8 flex flex-col" data-testid={`testimonial-${tst.id}`}>
                                <div className="font-serif text-5xl text-gold/40 leading-none mb-2">&ldquo;</div>
                                <blockquote className="font-serif text-lg text-navy leading-relaxed mb-6 flex-1">{tst.public_text}</blockquote>
                                <figcaption className="flex items-center gap-4 pt-5 border-t border-navy/10">
                                    {tst.photo && <img src={tst.photo} alt={tst.public_display_name} className="w-12 h-12 rounded-full object-cover" data-testid="testimonial-photo" />}
                                    <div>
                                        <div className="text-navy font-medium text-sm">{tst.public_display_name}</div>
                                        <div className="text-navy/55 text-xs uppercase tracking-wider mt-1">
                                            {[tst.community, tst.service_category].filter(Boolean).join(" · ")}
                                        </div>
                                    </div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
